// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import { useEffect, useMemo, useState } from 'react';
import { getApiPath, mergeClasses, normalizeNumber } from 'util/commons';
import { CheckBox } from 'components/atoms/CheckBox';
import { IconClipBoardList } from 'components/atoms/icons/IconClipBoardList';
import IconMinusCircle from 'components/atoms/icons/IconMinusCircle';
import { IconInfo } from 'components/atoms/icons/IconInfo';
import { IconDashboardCalendar } from 'components/atoms/icons/IconDashboardCalendar';
import { IconChevronRight } from 'components/atoms/icons/IconChevronRight';
import { IconEyeSlash } from 'components/atoms/icons/IconEyeSlash';
import { CollectRouteSiteInfo } from 'types/routeManagementTypes';
import { useHistory, useParams } from 'react-router';
import { Path } from 'routes';
import { useSelector } from 'react-redux';
import { selectCollectingRouteSites } from 'redux/slices/collectingRoute';
import CollectRouteSummary from 'components/molecules/CollectRouteSummary';
import { IconSubstitute } from 'components/atoms/icons/IconSubstitute';
import BigNumber from 'bignumber.js';
import Popover from 'components/atoms/Popover';
import { CollectingRouteSession } from 'util/collectingRoute';

type ConfirmVisitListProps = {
  mode?: 'confirm' | 'edit';
};

const ConfirmVisitList: React.FC<ConfirmVisitListProps> = ({
  mode = 'confirm',
}) => {
  const { t } = useTranslation();
  const [checkedMap, setCheckedMap] = useState<Record<string, boolean>>({});
  const { routeId } = useParams<{ routeId: string }>();
  const history = useHistory();
  const collectingRouteSites = useSelector(selectCollectingRouteSites);

  useEffect(() => {
    if (!collectingRouteSites) {
      return;
    }

    const isDefaultChecked = (site: CollectRouteSiteInfo) =>
      site.enableSubstituteRegist ||
      ((site.collectRequestFlg || site.emissionFlg) && site.weight !== '0');

    const collectingSession = CollectingRouteSession.get(routeId);

    const selectedMap = collectingSession?.siteMap || {};

    if (mode === 'confirm') {
      const selectedSiteIds = Object.keys(selectedMap).filter(
        (siteId) =>
          selectedMap[siteId] &&
          collectingRouteSites.sites.some((site) => site.id === siteId)
      );

      if (selectedSiteIds.length) {
        history.replace(
          `${getApiPath(Path.routeSiteSelection, { routeId })}${
            window.location.search
          }`
        );

        return;
      }

      CollectingRouteSession.remove(routeId);

      setCheckedMap(
        Object.fromEntries(
          collectingRouteSites.sites.map((site) => [
            site.id,
            isDefaultChecked(site),
          ])
        )
      );

      return;
    }

    if (!selectedMap) {
      setCheckedMap(
        Object.fromEntries(
          collectingRouteSites.sites.map((site) => [
            site.id,
            isDefaultChecked(site),
          ])
        )
      );

      return;
    }

    const newCheckedMap = Object.fromEntries(
      collectingRouteSites.sites.map((site) => [
        site.id,
        selectedMap[site.id] ?? isDefaultChecked(site),
      ])
    );

    CollectingRouteSession.save({
      routeId,
      siteMap: newCheckedMap,
    });
    setCheckedMap(newCheckedMap);
  }, [collectingRouteSites]);

  const canSave = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      Object.entries(checkedMap).filter(([_, checked]) => checked).length > 0,
    [checkedMap]
  );

  const getStatus = (site: CollectRouteSiteInfo) => {
    const infoColor = isSelected(site.id) ? '#86ea5c' : '#2e6d21';
    const substituteColor = isSelected(site.id) ? '#d2fdc6' : '#2e6d21';

    if (site.enableSubstituteRegist) {
      return {
        icon: <IconSubstitute fill={substituteColor} />,
        text: t('confirm_visit_list.substitute_registration'),
      };
    } else if (site.collectRequestFlg && site.weight !== '0') {
      return {
        icon: (
          <IconInfo
            width={22}
            height={22}
            fill={infoColor}
            stroke={infoColor}
          />
        ),
        text: t('confirm_visit_list.status.collection_requested'),
      };
    } else if (site.emissionFlg && site.weight !== '0') {
      return {
        icon: <IconClipBoardList strokeColor="#e7e7e7" />,
        text: t('confirm_visit_list.status.registered'),
      };
    } else {
      return {
        icon: <IconMinusCircle strokeColor="#5e5e5e" size={24} />,
        text: t('confirm_visit_list.status.no_registration'),
      };
    }
  };

  const isSelected = (siteId: string) => {
    return checkedMap[siteId];
  };

  const handleConfirmRoute = () => {
    if (!canSave) {
      return;
    }

    CollectingRouteSession.save({
      routeId,
      siteMap: checkedMap,
    });

    if (mode === 'confirm') {
      CollectingRouteSession.save({
        routeId,
        processedSiteIds: [],
      });
    }

    history.replace(
      `${getApiPath(Path.routeSiteSelection, { routeId })}${
        window.location.search
      }`
    );
  };

  const summary = useMemo(() => {
    return collectingRouteSites?.sites
      .filter(
        (site) =>
          !site.enableSubstituteRegist &&
          Number(site.weight) !== 0 &&
          isSelected(site.id)
      )
      .reduce(
        (sum, item) => {
          sum.quantity = sum.quantity.plus(item.quantity || 0);
          sum.weight = sum.weight.plus(item.weight || 0);
          return sum;
        },
        {
          quantity: new BigNumber(0),
          weight: new BigNumber(0),
        }
      );
  }, [collectingRouteSites?.sites, checkedMap]);

  return (
    <div className={styles.confirmVitsitWrapper}>
      <div className={styles.infoBar}>
        <div className={styles.confirmTitle}>
          <IconDashboardCalendar size={32} strokeColor="#86ea5c" />
          <div className={styles.confirmTitleText}>
            {collectingRouteSites?.routeName ??
              (mode === 'edit'
                ? t('confirm_visit_list.title_edit')
                : t('confirm_visit_list.title'))}
          </div>
        </div>

        <CollectRouteSummary
          weight={summary?.weight || 0}
          quantity={summary?.quantity || 0}
        />
      </div>

      {mode === 'confirm' && (
        <div className={styles.confirmNote}>{t('confirm_visit_list.note')}</div>
      )}

      <div className={styles.siteList}>
        {collectingRouteSites?.sites.length ? (
          collectingRouteSites?.sites.map((site) => (
            <div
              key={site.id}
              className={mergeClasses(styles.siteCard, {
                [styles.disabled]: !isSelected(site.id),
              })}
              onClick={() => {
                setCheckedMap((prev) => ({
                  ...prev,
                  [site.id]: !prev[site.id],
                }));
              }}
            >
              <div className={styles.siteCardLeft}>
                <div className={styles.select}>
                  {isSelected(site.id) ? (
                    <CheckBox
                      id={String(site.id)}
                      checked={isSelected(site.id)}
                      onChange={() => {}}
                      checkedStyle="green"
                    />
                  ) : (
                    <IconEyeSlash size={32} />
                  )}
                  <div className={styles.visit}>
                    {isSelected(site.id)
                      ? t('confirm_visit_list.visit')
                      : t('confirm_visit_list.exclude')}
                  </div>
                </div>

                <div className={styles.info}>
                  <div className={styles.status}>
                    {getStatus(site).icon}
                    <div
                      className={mergeClasses(styles.statusText, {
                        [styles.colorGreen]: site.collectRequestFlg,
                        [styles.colorGrey]:
                          !site.collectRequestFlg && !site.emissionFlg,
                        [styles.colorMint]: site.enableSubstituteRegist,
                      })}
                    >
                      {getStatus(site).text}
                    </div>
                  </div>
                  <div className={styles.name}>
                    <Popover
                      autoGen={{
                        lines: 2,
                        odd: 4,
                        wrapperStyle: {
                          width: 'fit-content',
                        },
                      }}
                      popUpStyle={{
                        fontSize: 16,
                      }}
                      positions={['top']}
                      content={site.name}
                      containerStyle={{
                        zIndex: '100000',
                      }}
                    >
                      <span>{site.name}</span>
                    </Popover>
                  </div>
                  <div className={styles.address}>
                    <Popover
                      autoGen={{
                        odd: 4,
                        wrapperStyle: {
                          width: 'fit-content',
                        },
                      }}
                      popUpStyle={{
                        fontSize: 16,
                      }}
                      positions={['top']}
                      content={site.address}
                      containerStyle={{
                        zIndex: '100000',
                      }}
                    >
                      <span>{site.address}</span>
                    </Popover>
                  </div>
                </div>
              </div>

              {!site.enableSubstituteRegist && (
                <div className={styles.overall}>
                  <div className={styles.quantity}>
                    <div className={styles.quantityTitle}>
                      {t('confirm_visit_list.overall.quantity')}
                    </div>
                    <div className={styles.quantityContent}>
                      <div className={styles.quantityNumber}>
                        <Popover
                          autoGen={{
                            odd: 4,
                            wrapperStyle: {
                              width: 'fit-content',
                            },
                          }}
                          popUpStyle={{
                            fontSize: 16,
                          }}
                          positions={['top']}
                          content={normalizeNumber({
                            value: site.quantity,
                          })}
                          containerStyle={{
                            zIndex: '100000',
                          }}
                        >
                          <span className={styles.siteAddress}>
                            {normalizeNumber({
                              value: site.quantity || 0,
                            })}
                          </span>
                        </Popover>
                      </div>
                      <div className={styles.unit}>
                        {t('confirm_visit_list.overall.quantity_unit')}
                      </div>
                    </div>
                  </div>
                  <div className={styles.weight}>
                    <div className={styles.weightTitle}>
                      {t('confirm_visit_list.overall.weight')}
                    </div>
                    <div className={styles.weightContent}>
                      <div className={styles.weightNumber}>
                        <Popover
                          autoGen={{
                            odd: 4,
                            wrapperStyle: {
                              width: 'fit-content',
                            },
                          }}
                          popUpStyle={{
                            fontSize: 16,
                          }}
                          positions={['top']}
                          content={normalizeNumber({
                            value: site.weight,
                          })}
                          containerStyle={{
                            zIndex: '100000',
                          }}
                        >
                          <span className={styles.siteAddress}>
                            {normalizeNumber({
                              value: site.weight || 0,
                            })}
                          </span>
                        </Popover>
                      </div>
                      <div className={styles.unit}>kg</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className={styles.noSite}>{t('messages.M_008')}</div>
        )}
      </div>

      <div className={styles.bottom}>
        {(mode === 'edit' || routeId !== 'all') && (
          <button
            className={styles.cancelBtn}
            onClick={() => {
              if (mode === 'confirm') {
                CollectingRouteSession.remove(routeId);
                history.push(`${Path.routeList}${window.location.search}`);
              } else {
                history.replace(
                  `${getApiPath(Path.routeSiteSelection, { routeId })}${
                    window.location.search
                  }`
                );
              }
            }}
          >
            {t('common.button.cancel')}
          </button>
        )}

        <button
          className={styles.confirmBtn}
          disabled={!canSave}
          onClick={handleConfirmRoute}
        >
          {mode === 'confirm' ? (
            <>
              {t('confirm_visit_list.button_confirm')}
              <IconChevronRight
                width={14}
                height={24}
                strokeColor="#1b1b1b"
                strokeOpacity={1}
              />
            </>
          ) : (
            t('common.button.save')
          )}
        </button>
      </div>
    </div>
  );
};

export default ConfirmVisitList;
